/**
 * Authentication Error Handling Utilities
 *
 * Translates AWS Cognito error codes to user-friendly Polish messages
 */

/**
 * Cognito error codes and their Polish translations
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Sign in errors
  UserNotFoundException: 'Użytkownik o podanym adresie email nie istnieje',
  NotAuthorizedException: 'Nieprawidłowe dane logowania',
  UserNotConfirmedException: 'Konto nie zostało aktywowane. Sprawdź swoją skrzynkę email i kliknij w link aktywacyjny. Jeśli nie otrzymałeś emaila, sprawdź folder SPAM lub skontaktuj się z administratorem.',
  PasswordResetRequiredException: 'Wymagane jest zresetowanie hasła',

  // Sign up errors
  UsernameExistsException: 'Użytkownik o podanym adresie email już istnieje',
  InvalidPasswordException: 'Hasło nie spełnia wymagań bezpieczeństwa',
  InvalidParameterException: 'Nieprawidłowe parametry. Sprawdź poprawność danych.',

  // Verification errors
  CodeMismatchException: 'Nieprawidłowy kod weryfikacyjny',
  ExpiredCodeException: 'Kod weryfikacyjny wygasł. Poproś o nowy kod.',

  // Rate limiting
  LimitExceededException: 'Zbyt wiele prób. Spróbuj ponownie za chwilę.',
  TooManyRequestsException: 'Zbyt wiele żądań. Spróbuj ponownie za chwilę.',
  TooManyFailedAttemptsException: 'Zbyt wiele nieudanych prób. Spróbuj ponownie później.',

  // Network errors
  NetworkError: 'Błąd połączenia. Sprawdź połączenie z internetem.',

  // API authorization errors (expired session, invalid token)
  UnauthorizedException: 'Sesja wygasła. Zaloguj się ponownie.',

  // Generic errors
  InternalErrorException: 'Wystąpił błąd serwera. Spróbuj ponownie później.',
  ServiceUnavailable: 'Usługa chwilowo niedostępna. Spróbuj ponownie później.',
};

/**
 * Translates AWS Cognito error to user-friendly Polish message
 *
 * @param error - Error object from AWS Amplify Auth
 * @returns User-friendly error message in Polish
 */
export function getAuthErrorMessage(error: unknown): string {
  // Handle Error objects
  if (error instanceof Error) {
    const errorName = error.name;

    // Check if we have a translation for this error code
    if (errorName in ERROR_MESSAGES) {
      return ERROR_MESSAGES[errorName];
    }

    // Check error message for known patterns
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return ERROR_MESSAGES.NetworkError;
    }

    if (message.includes('password')) {
      return 'Nieprawidłowe hasło. Hasło musi zawierać co najmniej 8 znaków, w tym wielkie i małe litery, cyfry oraz znaki specjalne.';
    }

    // Return original error message as fallback
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Default fallback
  return 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';
}

/**
 * Checks if an error is an authentication/authorization error (expired session, invalid token).
 * Use in catch blocks to distinguish auth errors from other API errors.
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const name = error.name;
    const msg = error.message.toLowerCase();

    // Cognito / AppSync error names
    if (name === 'UnauthorizedException' || name === 'NotAuthorizedException') {
      return true;
    }

    // Amplify SDK error messages
    if (
      msg.includes('not authenticated') ||
      msg.includes('no current user') ||
      msg.includes('unauthenticated') ||
      msg.includes('token has expired') ||
      msg.includes('unauthorized')
    ) {
      return true;
    }
  }

  // GraphQL error array from Amplify client
  if (Array.isArray(error)) {
    return error.some(
      (e) =>
        typeof e?.message === 'string' &&
        (e.message.includes('Unauthorized') || e.message.includes('Not Authorized'))
    );
  }

  return false;
}

/**
 * Returns a user-friendly Polish message for API errors.
 * Auth errors get a specific message; others fall through to the original message.
 */
export function getApiErrorMessage(error: unknown): string {
  if (isAuthError(error)) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';
}

/**
 * Validates email format
 *
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates password strength according to Cognito requirements
 * Requirements from amplify_outputs.json:
 * - min_length: 8
 * - require_lowercase: true
 * - require_uppercase: true
 * - require_numbers: true
 * - require_symbols: true
 *
 * @param password - Password to validate
 * @returns Object with validation result and message
 */
export function validatePassword(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Hasło musi mieć co najmniej 8 znaków' };
  }

  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Hasło musi zawierać małe litery' };
  }

  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Hasło musi zawierać wielkie litery' };
  }

  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Hasło musi zawierać cyfry' };
  }

  if (!/[^a-zA-Z0-9]/.test(password)) {
    return { valid: false, message: 'Hasło musi zawierać znaki specjalne (np. !@#$%^&*)' };
  }

  return { valid: true };
}
